import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Card, Row, Col, Typography, Input, Space, Modal, List, Tag, Tooltip, Alert,
    Spin, Switch, Segmented, Drawer, Tabs, Popconfirm, Empty
} from 'antd';
import Header from '../components/Header';
import Footer from '../components/Footer';
import StandardButton from '../components/StandardButton';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import api from '../interceptors/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

const DRAFT_KEY = 'quto.html-playground.draft';
const LIVE_KEY = 'quto.html-playground.live';
const LIVE_DEBOUNCE_MS = 350;
const DRAFT_DEBOUNCE_MS = 500;
const EDITOR_HEIGHT = 600;

const DEFAULT_HTML = `<!DOCTYPE html>
<html>
<head>
    <style>
        /* Add your CSS here */
        body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 20px;
            background: #f5f5f5;
        }
        .demo-area {
            background: white;
            padding: 30px;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            max-width: 800px;
            margin: 0 auto;
        }
    </style>
</head>
<body>
    <div class="demo-area">
        <h1>Your HTML Playground</h1>
        <p>Start editing your code here!</p>
    </div>

    <script>
        // Add your JavaScript here
        console.log('Hello from HTML Playground!');
    </script>
</body>
</html>`;

const readStorage = (key) => {
    try { return localStorage.getItem(key); } catch (_) { return null; }
};
const writeStorage = (key, value) => {
    try { localStorage.setItem(key, value); } catch (_) { /* storage unavailable */ }
};

const getUserData = () => {
    try {
        return JSON.parse(localStorage.getItem('userProfileData') || 'null');
    } catch (_) {
        return null;
    }
};

const isAuthenticated = () => {
    const user = getUserData();
    return !!(user?.id && (user.google_id || user.linkedin_id));
};

const copyText = async (text) => {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch (_) {
        return false;
    }
};

// First meaningful line of a snippet, for list previews.
const snippetPreview = (code, maxLength = 120) => {
    if (!code) return '';
    const line = code
        .split('\n')
        .map((l) => l.trim())
        .find((l) => l && !l.startsWith('<!DOCTYPE') && l !== '<html>' && l !== '<head>' && !l.startsWith('<style'))
        || code.trim();
    return line.length > maxLength ? `${line.slice(0, maxLength)}…` : line;
};

const iframeStyle = { width: '100%', height: '100%', border: 'none' };
const previewFrameStyle = {
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    height: EDITOR_HEIGHT,
    overflow: 'hidden',
    backgroundColor: 'white',
};
const editorStyle = {
    fontSize: 13,
    lineHeight: 1.5,
    height: EDITOR_HEIGHT,
    resize: 'vertical',
    tabSize: 2,
};
const linkBoxStyle = {
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
    fontSize: 12,
    background: '#f5f5f5',
    border: '1px solid #e5e5e5',
    borderRadius: 6,
    padding: '6px 10px',
    wordBreak: 'break-all',
    flex: 1,
};

const HtmlPlayground = () => {
    const { t } = useTranslation();
    const { lang = 'en' } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const basePath = `/${lang}/tools/html-playground`;
    const editorUrlFor = useCallback((id) => `${window.location.origin}${basePath}?share=${id}`, [basePath]);
    const liveUrlFor = useCallback((id) => `${window.location.origin}${basePath}/preview?id=${id}`, [basePath]);

    const [htmlCode, setHtmlCode] = useState(DEFAULT_HTML);
    const [previewCode, setPreviewCode] = useState(DEFAULT_HTML);
    const [previewKey, setPreviewKey] = useState(0);
    const [live, setLive] = useState(() => readStorage(LIVE_KEY) !== 'off');
    const [layout, setLayout] = useState('split');
    const [sharedId, setSharedId] = useState(null);
    const [shareResult, setShareResult] = useState(null);
    const [loginPromptOpen, setLoginPromptOpen] = useState(false);
    const [workOpen, setWorkOpen] = useState(false);
    const [workTab, setWorkTab] = useState('saved');
    const [history, setHistory] = useState([]);
    const [shares, setShares] = useState([]);
    const [loading, setLoading] = useState({ history: false, shares: false, save: false, share: false, loadingShare: false });
    const [draftSaved, setDraftSaved] = useState(false);

    const htmlCodeRef = useRef(htmlCode);
    const loadedShareIdRef = useRef(null);
    const draftRestoredRef = useRef(false);
    const authenticated = isAuthenticated();

    useEffect(() => { htmlCodeRef.current = htmlCode; }, [htmlCode]);

    useEffect(() => {
        document.title = `${t('html_playground_title')} | quto.dev`;
        return () => { document.title = 'quto.dev'; };
    }, [t]);

    // ---------- preview ----------
    const runPreview = useCallback(() => {
        setPreviewCode(htmlCodeRef.current);
        setPreviewKey((k) => k + 1);
    }, []);

    useEffect(() => {
        if (!live) return undefined;
        const timer = setTimeout(() => setPreviewCode(htmlCode), LIVE_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [htmlCode, live]);

    const handleLiveToggle = (checked) => {
        setLive(checked);
        writeStorage(LIVE_KEY, checked ? 'on' : 'off');
        if (checked) setPreviewCode(htmlCodeRef.current);
    };

    // ---------- draft autosave ----------
    useEffect(() => {
        const timer = setTimeout(() => {
            writeStorage(DRAFT_KEY, htmlCode);
            setDraftSaved(true);
        }, DRAFT_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [htmlCode]);

    // ---------- shared snippet loading ----------
    const loadSharedCode = useCallback(async (shareId) => {
        // Mark before fetching so a re-run of the effect (e.g. after i18n loads) doesn't fetch twice.
        loadedShareIdRef.current = shareId;
        setLoading((prev) => ({ ...prev, loadingShare: true }));
        try {
            const response = await api.get(`/html-playground/share/${shareId}`);
            const code = response.data?.html_code;
            if (typeof code !== 'string') throw new Error('Invalid share data');
            setHtmlCode(code);
            htmlCodeRef.current = code;
            setPreviewCode(code);
            setSharedId(shareId);
            toast.success(t('shared_code_loaded'));
        } catch (_) {
            loadedShareIdRef.current = null;
            toast.error(t('share_not_found'));
            navigate(basePath, { replace: true });
        } finally {
            setLoading((prev) => ({ ...prev, loadingShare: false }));
        }
    }, [basePath, navigate, t]);

    useEffect(() => {
        const shareId = searchParams.get('share');
        if (shareId) {
            if (shareId !== loadedShareIdRef.current) loadSharedCode(shareId);
            return;
        }
        if (!draftRestoredRef.current) {
            draftRestoredRef.current = true;
            const draft = readStorage(DRAFT_KEY);
            if (draft && draft !== DEFAULT_HTML) {
                setHtmlCode(draft);
                htmlCodeRef.current = draft;
                setPreviewCode(draft);
                toast(t('hp_draft_restored'), { icon: '📝' });
            }
        }
    }, [searchParams, loadSharedCode, t]);

    const leaveSharedView = useCallback(() => {
        if (!sharedId) return;
        setSharedId(null);
        loadedShareIdRef.current = null;
        navigate(basePath, { replace: true });
    }, [sharedId, basePath, navigate]);

    // ---------- editor ----------
    const handleCodeChange = useCallback((e) => {
        const next = e.target.value;
        setHtmlCode(next);
        htmlCodeRef.current = next;
        setDraftSaved(false);
        leaveSharedView();
    }, [leaveSharedView]);

    const handleEditorKeyDown = useCallback((e) => {
        if (e.key !== 'Tab') return;
        e.preventDefault();
        const el = e.target;
        const start = el.selectionStart;
        const end = el.selectionEnd;
        const next = `${htmlCodeRef.current.slice(0, start)}  ${htmlCodeRef.current.slice(end)}`;
        setHtmlCode(next);
        htmlCodeRef.current = next;
        setDraftSaved(false);
        leaveSharedView();
        requestAnimationFrame(() => { el.selectionStart = el.selectionEnd = start + 2; });
    }, [leaveSharedView]);

    const replaceCode = useCallback((code) => {
        setHtmlCode(code);
        htmlCodeRef.current = code;
        setPreviewCode(code);
        setPreviewKey((k) => k + 1);
    }, []);

    const handleReset = useCallback(() => {
        replaceCode(DEFAULT_HTML);
        leaveSharedView();
        toast.success(t('reset_to_default'));
    }, [replaceCode, leaveSharedView, t]);

    const handleCopy = useCallback(async () => {
        if (await copyText(htmlCodeRef.current)) toast.success(t('html_copied'));
        else toast.error(t('copy_failed'));
    }, [t]);

    const handleDownload = useCallback(() => {
        const blob = new Blob([htmlCodeRef.current], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'html-playground.html';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(t('html_downloaded'));
    }, [t]);

    const handleOpenNewTab = useCallback(() => {
        const blob = new Blob([htmlCodeRef.current], { type: 'text/html' });
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
    }, []);

    // ---------- account: history & shares ----------
    const loadHistory = useCallback(async () => {
        if (!isAuthenticated()) { setHistory([]); return; }
        setLoading((prev) => ({ ...prev, history: true }));
        try {
            const response = await api.get('/html-playground/history');
            setHistory(Array.isArray(response.data?.history) ? response.data.history : []);
        } catch (error) {
            toast.error(error.response?.status === 401 ? t('authentication_required') : t('failed_to_load_history'));
            setHistory([]);
        } finally {
            setLoading((prev) => ({ ...prev, history: false }));
        }
    }, [t]);

    const loadShares = useCallback(async () => {
        if (!isAuthenticated()) { setShares([]); return; }
        setLoading((prev) => ({ ...prev, shares: true }));
        try {
            const response = await api.get('/html-playground/shares');
            setShares(Array.isArray(response.data?.shares) ? response.data.shares : []);
        } catch (error) {
            toast.error(error.response?.status === 401 ? t('authentication_required') : t('failed_to_load_shares'));
            setShares([]);
        } finally {
            setLoading((prev) => ({ ...prev, shares: false }));
        }
    }, [t]);

    const openWork = useCallback((tab = 'saved') => {
        setWorkTab(tab);
        setWorkOpen(true);
        if (isAuthenticated()) {
            loadHistory();
            loadShares();
        }
    }, [loadHistory, loadShares]);

    const goToLogin = useCallback(() => {
        const redirect = `${window.location.pathname}${window.location.search}`;
        navigate(`/${lang}/auth/login?redirect=${encodeURIComponent(redirect)}`);
    }, [navigate, lang]);

    const handleSave = useCallback(async () => {
        if (!isAuthenticated()) { setLoginPromptOpen(true); return; }
        const code = htmlCodeRef.current;
        if (!code.trim()) { toast.error(t('code_empty')); return; }
        setLoading((prev) => ({ ...prev, save: true }));
        try {
            await api.post('/html-playground/save-history', { html_code: code });
            toast.success(t('saved_to_history'));
            if (workOpen) loadHistory();
        } catch (error) {
            toast.error(error.response?.status === 401 ? t('authentication_required') : t('failed_to_save_history'));
        } finally {
            setLoading((prev) => ({ ...prev, save: false }));
        }
    }, [t, workOpen, loadHistory]);

    const handleShare = useCallback(async () => {
        const code = htmlCodeRef.current;
        if (!code.trim()) { toast.error(t('code_empty')); return; }
        setLoading((prev) => ({ ...prev, share: true }));
        try {
            const response = await api.post('/html-playground/share', { html_code: code });
            const id = response.data?.share_id || response.data?.id;
            if (!id) throw new Error('No share id');
            const editorUrl = editorUrlFor(id);
            const copied = await copyText(editorUrl);
            setShareResult({ id, editorUrl, liveUrl: liveUrlFor(id), anonymous: !isAuthenticated(), copied });
            if (workOpen) loadShares();
        } catch (_) {
            toast.error(t('share_failed'));
        } finally {
            setLoading((prev) => ({ ...prev, share: false }));
        }
    }, [t, editorUrlFor, liveUrlFor, workOpen, loadShares]);

    const handleLoadFromHistory = (item) => {
        replaceCode(item.html_code);
        leaveSharedView();
        setWorkOpen(false);
        toast.success(t('history_loaded'));
    };

    const handleDeleteHistory = async (id) => {
        try {
            await api.delete(`/html-playground/history/${id}`);
            toast.success(t('history_deleted'));
            loadHistory();
        } catch (_) {
            toast.error(t('failed_to_delete_history'));
        }
    };

    const handleClearAllHistory = async () => {
        try {
            await api.delete('/html-playground/history');
            toast.success(t('all_history_deleted'));
            loadHistory();
        } catch (_) {
            toast.error(t('failed_to_clear_history'));
        }
    };

    const handleDeleteShare = async (shareId) => {
        try {
            await api.delete(`/html-playground/share/${shareId}`);
            toast.success(t('share_deleted'));
            loadShares();
        } catch (error) {
            const status = error.response?.status;
            toast.error(status === 403 ? t('unauthorized_delete') : status === 404 ? t('share_not_found') : t('failed_to_delete_share'));
        }
    };

    const copyLink = async (url) => {
        if (await copyText(url)) toast.success(t('copied_to_clipboard'));
        else toast.error(t('copy_failed'));
    };

    // ---------- keyboard shortcuts ----------
    useEffect(() => {
        const onKeyDown = (e) => {
            if (!(e.ctrlKey || e.metaKey)) return;
            if (e.key === 'Enter') { e.preventDefault(); runPreview(); }
            else if (e.key.toLowerCase() === 's') { e.preventDefault(); handleSave(); }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [runPreview, handleSave]);

    const formatDate = (value) => (value ? new Date(value).toLocaleString() : '');
    const lineCount = useMemo(() => (htmlCode ? htmlCode.split('\n').length : 0), [htmlCode]);
    const showEditor = layout !== 'preview';
    const showPreview = layout !== 'code';
    const colSpan = layout === 'split' ? 12 : 24;

    const layoutOptions = [
        { label: t('hp_layout_split'), value: 'split' },
        { label: t('hp_layout_code'), value: 'code' },
        { label: t('hp_layout_preview'), value: 'preview' },
    ];

    const renderLinkRow = (label, help, url) => (
        <div className="mb-4">
            <Text strong>{label}</Text>
            <div className="flex items-center gap-2 mt-1">
                <div style={linkBoxStyle}>{url}</div>
                <StandardButton type="secondary" size="small" onClick={() => copyLink(url)}>{t('copy')}</StandardButton>
                <StandardButton type="secondary" size="small" onClick={() => window.open(url, '_blank')}>{t('open')}</StandardButton>
            </div>
            <Text type="secondary" className="text-xs">{help}</Text>
        </div>
    );

    return (
        <div className="min-h-screen bg-gray-50">
            <Header customHeading={t('html_playground_title')} />

            <div className="max-w-7xl mx-auto p-4">
                {/* Title + primary actions */}
                <Card className="mb-4">
                    <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                        <div>
                            <Title level={2} className="!mb-1">{t('html_playground_title')}</Title>
                            <Text type="secondary">{t('html_playground_description')}</Text>
                        </div>
                        <Space wrap>
                            <StandardButton type="secondary" onClick={() => openWork('saved')}>
                                {t('hp_my_work')}
                            </StandardButton>
                            <Tooltip title={authenticated ? t('hp_save_tip') : t('hp_login_to_save_desc')}>
                                <StandardButton
                                    type="secondary"
                                    onClick={handleSave}
                                    loading={loading.save}
                                    disabled={!htmlCode.trim()}
                                >
                                    {t('save')}
                                </StandardButton>
                            </Tooltip>
                            <Tooltip title={t('hp_share_tip')}>
                                <StandardButton
                                    type="primary"
                                    onClick={handleShare}
                                    loading={loading.share}
                                    disabled={!htmlCode.trim()}
                                >
                                    {t('share')}
                                </StandardButton>
                            </Tooltip>
                        </Space>
                    </div>
                </Card>

                {sharedId && (
                    <Alert
                        type="info"
                        showIcon
                        className="mb-4"
                        message={t('hp_shared_banner')}
                        action={
                            <Space wrap>
                                <StandardButton type="secondary" size="small" onClick={() => copyLink(editorUrlFor(sharedId))}>
                                    {t('copy_link')}
                                </StandardButton>
                                <StandardButton type="secondary" size="small" onClick={() => window.open(liveUrlFor(sharedId), '_blank')}>
                                    {t('hp_open_live')}
                                </StandardButton>
                                <StandardButton type="secondary" size="small" onClick={handleReset}>
                                    {t('hp_start_fresh')}
                                </StandardButton>
                            </Space>
                        }
                    />
                )}

                {/* Toolbar */}
                <Card className="mb-4" styles={{ body: { padding: '10px 16px' } }}>
                    <div className="flex flex-wrap items-center gap-3">
                        <Segmented options={layoutOptions} value={layout} onChange={setLayout} />
                        <Tooltip title={live ? t('hp_live_on_tip') : t('hp_live_off_tip')}>
                            <span className="flex items-center gap-2">
                                <Switch checked={live} onChange={handleLiveToggle} size="small" />
                                <Text>{t('hp_live')}</Text>
                            </span>
                        </Tooltip>
                        {!live && (
                            <StandardButton type="primary" size="small" onClick={runPreview}>
                                {t('hp_run')}
                            </StandardButton>
                        )}
                        <div className="flex-1" />
                        <Space wrap>
                            <StandardButton type="secondary" size="small" onClick={handleCopy}>{t('copy')}</StandardButton>
                            <StandardButton type="secondary" size="small" onClick={handleDownload}>{t('download_html')}</StandardButton>
                            <StandardButton type="secondary" size="small" onClick={handleOpenNewTab}>{t('hp_open_new_tab')}</StandardButton>
                            <Popconfirm title={t('hp_reset_confirm')} onConfirm={handleReset} okText={t('reset_to_default')} cancelText={t('cancel')}>
                                <StandardButton type="secondary" size="small" danger>{t('reset_to_default')}</StandardButton>
                            </Popconfirm>
                        </Space>
                    </div>
                </Card>

                {loading.loadingShare && (
                    <div className="text-center py-4">
                        <Spin />
                        <p className="mt-2">{t('loading_shared_code')}</p>
                    </div>
                )}

                <Row gutter={[16, 16]}>
                    {showEditor && (
                        <Col xs={24} lg={colSpan}>
                            <Card
                                title={t('hp_layout_code')}
                                extra={
                                    <Text type="secondary" className="text-xs">
                                        {lineCount} {t('hp_lines')}{draftSaved ? ` · ${t('hp_draft_saved')}` : ''}
                                    </Text>
                                }
                            >
                                <TextArea
                                    value={htmlCode}
                                    onChange={handleCodeChange}
                                    onKeyDown={handleEditorKeyDown}
                                    className="hp-editor"
                                    style={editorStyle}
                                    placeholder={t('enter_html_css_placeholder')}
                                    disabled={loading.loadingShare}
                                    spellCheck={false}
                                />
                            </Card>
                        </Col>
                    )}
                    {showPreview && (
                        <Col xs={24} lg={colSpan}>
                            <Card
                                title={
                                    <Space>
                                        <span>{t('hp_layout_preview')}</span>
                                        <Tag color={live ? 'green' : 'default'}>{live ? t('hp_live') : t('hp_paused')}</Tag>
                                    </Space>
                                }
                                extra={
                                    <Tooltip title={t('hp_reload_preview_tip')}>
                                        <StandardButton type="secondary" size="small" onClick={runPreview}>
                                            {t('hp_reload_preview')}
                                        </StandardButton>
                                    </Tooltip>
                                }
                            >
                                <div style={previewFrameStyle}>
                                    <iframe
                                        key={previewKey}
                                        srcDoc={previewCode}
                                        title="HTML Preview"
                                        style={iframeStyle}
                                        sandbox="allow-scripts allow-same-origin allow-forms allow-modals allow-popups"
                                    />
                                </div>
                            </Card>
                        </Col>
                    )}
                </Row>

                <Text type="secondary" className="text-xs block mt-3">{t('hp_shortcuts')}</Text>

                {/* Share result */}
                <Modal
                    title={t('hp_share_ready_title')}
                    open={!!shareResult}
                    onCancel={() => setShareResult(null)}
                    footer={null}
                    width={640}
                >
                    {shareResult && (
                        <div>
                            {shareResult.copied && (
                                <Alert type="success" showIcon message={t('link_copied')} className="mb-4" />
                            )}
                            {renderLinkRow(t('hp_share_editor_link'), t('hp_share_editor_link_help'), shareResult.editorUrl)}
                            {renderLinkRow(t('hp_share_live_link'), t('hp_share_live_link_help'), shareResult.liveUrl)}
                            <Text type="secondary" className="text-xs">
                                {shareResult.anonymous ? t('hp_share_anonymous_note') : t('hp_share_manage_note')}
                            </Text>
                            {shareResult.anonymous && (
                                <div className="mt-3">
                                    <StandardButton type="secondary" size="small" onClick={goToLogin}>{t('login')}</StandardButton>
                                </div>
                            )}
                        </div>
                    )}
                </Modal>

                {/* Login prompt for Save */}
                <Modal
                    title={t('hp_login_cta')}
                    open={loginPromptOpen}
                    onCancel={() => setLoginPromptOpen(false)}
                    footer={
                        <Space>
                            <StandardButton type="secondary" onClick={() => setLoginPromptOpen(false)}>{t('cancel')}</StandardButton>
                            <StandardButton type="primary" onClick={goToLogin}>{t('login')}</StandardButton>
                        </Space>
                    }
                >
                    <p>{t('hp_login_to_save_desc')}</p>
                    <Text type="secondary" className="text-xs">{t('hp_login_share_instead')}</Text>
                </Modal>

                {/* My work */}
                <Drawer
                    title={t('hp_my_work')}
                    open={workOpen}
                    onClose={() => setWorkOpen(false)}
                    width="min(560px, 100vw)"
                >
                    {!authenticated ? (
                        <div className="text-center py-8">
                            <p className="mb-4">{t('hp_login_to_view_work')}</p>
                            <StandardButton type="primary" onClick={goToLogin}>{t('login')}</StandardButton>
                        </div>
                    ) : (
                        <Tabs
                            activeKey={workTab}
                            onChange={setWorkTab}
                            items={[
                                {
                                    key: 'saved',
                                    label: `${t('hp_saved_tab')} (${history.length})`,
                                    children: loading.history ? (
                                        <div className="text-center py-8"><Spin /></div>
                                    ) : (
                                        <div>
                                            {history.length > 0 && (
                                                <div className="flex justify-end mb-2">
                                                    <Popconfirm title={t('hp_clear_all_confirm')} onConfirm={handleClearAllHistory} okText={t('delete')} cancelText={t('cancel')}>
                                                        <StandardButton type="secondary" size="small" danger>{t('clear_all')}</StandardButton>
                                                    </Popconfirm>
                                                </div>
                                            )}
                                            <List
                                                dataSource={history}
                                                locale={{ emptyText: <Empty description={t('hp_saved_empty')} /> }}
                                                renderItem={(item) => (
                                                    <List.Item
                                                        actions={[
                                                            <StandardButton key="load" type="primary" size="small" onClick={() => handleLoadFromHistory(item)}>{t('hp_load')}</StandardButton>,
                                                            <Popconfirm key="del" title={t('hp_delete_confirm')} onConfirm={() => handleDeleteHistory(item.id)} okText={t('delete')} cancelText={t('cancel')}>
                                                                <StandardButton type="secondary" size="small" danger>{t('delete')}</StandardButton>
                                                            </Popconfirm>,
                                                        ]}
                                                    >
                                                        <List.Item.Meta
                                                            title={formatDate(item.timestamp || item.created_at)}
                                                            description={<span className="font-mono text-xs">{snippetPreview(item.html_code)}</span>}
                                                        />
                                                    </List.Item>
                                                )}
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    key: 'shared',
                                    label: `${t('hp_shared_tab')} (${shares.length})`,
                                    children: loading.shares ? (
                                        <div className="text-center py-8"><Spin /></div>
                                    ) : (
                                        <List
                                            dataSource={shares}
                                            locale={{ emptyText: <Empty description={t('hp_shared_empty')} /> }}
                                            renderItem={(item) => {
                                                const id = item.share_id;
                                                return (
                                                    <List.Item>
                                                        <List.Item.Meta
                                                            title={
                                                                <Space>
                                                                    <span>{formatDate(item.created_at)}</span>
                                                                    <Tag>{item.views ?? 0} {t('views')}</Tag>
                                                                </Space>
                                                            }
                                                            description={
                                                                <div>
                                                                    <div className="font-mono text-xs mb-2">{snippetPreview(item.html_code)}</div>
                                                                    <Space wrap>
                                                                        <StandardButton type="primary" size="small" onClick={() => { setWorkOpen(false); navigate(`${basePath}?share=${id}`); }}>{t('open')}</StandardButton>
                                                                        <StandardButton type="secondary" size="small" onClick={() => copyLink(editorUrlFor(id))}>{t('copy_link')}</StandardButton>
                                                                        <StandardButton type="secondary" size="small" onClick={() => window.open(liveUrlFor(id), '_blank')}>{t('hp_open_live')}</StandardButton>
                                                                        <Popconfirm title={t('hp_delete_confirm')} onConfirm={() => handleDeleteShare(id)} okText={t('delete')} cancelText={t('cancel')}>
                                                                            <StandardButton type="secondary" size="small" danger>{t('delete')}</StandardButton>
                                                                        </Popconfirm>
                                                                    </Space>
                                                                </div>
                                                            }
                                                        />
                                                    </List.Item>
                                                );
                                            }}
                                        />
                                    ),
                                },
                            ]}
                        />
                    )}
                </Drawer>
            </div>

            <Footer />
        </div>
    );
};

export default HtmlPlayground;
